import styled from 'styled-components';


import * as Text from '../../styles/shared/typography';
import * as colors from '../../styles/shared/colors';
import { respondTo } from '../../styles/shared/breakpoints';


const isiBodyFontSize = '16px';
const safetyFontSize = '16px';
const safetyLineHeight = '20px';


const highlightStyles = `
 .blue-highlight {
   color: ${colors.weliregBlue};
   font-weight: 700;
 }
 .green-highlight {
   color: #008542;
   font-weight: 700;
 }
 .purple-highlight {
   color: #8c2d8c;
   font-weight: 700;
 }
 .spacing-expand {
   display: inline-block;
   margin-top: 6px;
   letter-spacing: 0.1px;
 }
 .darkColor {
   color: ${colors.black};
 }
 .nowrap {
   white-space: nowrap;
 }
`;


export const SafetyListContainer = styled.section`
 ${highlightStyles}
 margin: 0;
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 align-self: stretch;
 gap: 20px;
 padding: 20px;
 background: ${colors.white};
 width: 100%;
 box-sizing: border-box;


 ${respondTo.md`
   padding: 32px 30px;
 `}
`;


const columnBulletOverrides = (bulletColor, bulletTop = '7px') => `
 margin-bottom: 0;
 font-size: ${safetyFontSize};
 line-height: ${safetyLineHeight};
 color: ${colors.black};
 list-style: none;


 li {
   position: relative;
   padding-left: 16px;
   margin-bottom: 8px;
   font-size: ${safetyFontSize};
   line-height: ${safetyLineHeight};
   color: ${colors.black};
 }


 li::before {
   content: '';
   position: absolute;
   left: 0;
   top: ${bulletTop};
   display: inline-block;
   box-sizing: border-box;
   width: 5px;
   height: 5px;
   min-width: 5px;
   min-height: 5px;
   aspect-ratio: 1 / 1;
   border-radius: 50%;
   flex-shrink: 0;
   background-color: ${bulletColor};
 }


 ul.sub-list {
   margin-top: 6px;
   margin-left: -16px;
   padding-left: 0;
   list-style: none;
 }


 ul.sub-list li {
   padding-left: 16px;
   margin-bottom: 10px;
 }


 ul.sub-list li::before {
   top: ${bulletTop};
   display: inline-block;
   box-sizing: border-box;
   width: 5px;
   height: 5px;
   min-width: 5px;
   min-height: 5px;
   aspect-ratio: 1 / 1;
   border-radius: 50%;
   flex-shrink: 0;
   background-color: ${bulletColor};
 }
`;


export const BulletList = styled.ul`
 ${columnBulletOverrides('#3F89C8', '7px')}
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 gap: 8px;
 align-self: stretch;
 margin: 0;
 padding: 0;


 li {
   margin: 0;
 }
`;


export const KeytrudaBulletList = styled.ul`
 ${columnBulletOverrides('#6CC044', '7.5px')}
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 gap: 8px;
 align-self: stretch;
 margin: 0;
 padding: 0;


 li {
   margin: 0;
 }
`;


export const WarningBulletList = styled.ul`
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 align-self: stretch;
 gap: 2px;
 margin: 0;
 padding: 0;
 width: 100%;
 list-style: none;


 li {
   display: flex;
   align-items: flex-start;
   gap: 12px;
   align-self: stretch;
   margin: 0;
   padding: 0;
   color: #000;
   font-size: 16px;
   font-style: normal;
   font-weight: 700;
   line-height: 20px;
   letter-spacing: -0.16px;


   &:last-child {
     padding-bottom: 0;
   }
 }


 /* Same blue bullet size + first-line align as BulletList */
 li::before {
   content: '';
   position: static;
   flex-shrink: 0;
   display: inline-block;
   box-sizing: border-box;
   width: 5px;
   height: 5px;
   min-width: 5px;
   min-height: 5px;
   aspect-ratio: 1 / 1;
   margin-top: 7px;
   border-radius: 50%;
   background-color: #3F89C8;
 }
`;


export const SafetyGrid = styled.div`
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 align-self: stretch;
 gap: 40px;
 width: 100%;
`;


export const ColumnsRow = styled.div`
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 align-self: stretch;
 gap: 40px;
 width: 100%;
 position: relative;


 ${respondTo.md`
   flex-direction: row;
   gap: 80px;
 `}
`;


export const LeftColumn = styled.div`
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 gap: 16px;
 flex: 1 0 0;
 min-width: 0;
 padding: 0;
 align-self: stretch;
`;


// title + WARNING — gap 12px (no flex-grow — avoids stretching before Anemia)
export const WeliregColumnIntro = styled.div`
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 gap: 12px;
 align-self: stretch;
 width: 100%;
 flex: 0 0 auto;
`;


// Anemia + sections — gap 16px (space-S)
export const WeliregColumnBody = styled.div`
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 gap: 16px;
 align-self: stretch;
 width: 100%;
`;


export const RightColumn = styled.div`
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 gap: 16px;
 flex: 1 0 0;
 min-width: 0;
 padding: 0;
 align-self: stretch;
`;


// KEYTRUDA title → first section (Contraindications) — gap 12px
export const KeytrudaColumnIntro = styled.div`
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 gap: 12px;
 flex: 1 0 0;
 align-self: stretch;
 width: 100%;
`;


export const KeytrudaColumnBody = styled(WeliregColumnBody)``;


const ColumnTitle = styled(Text.H4)`
 color: ${colors.black};
 font-size: 24px;
 line-height: 24px;
 letter-spacing: -0.24px;
 margin: 0;
 font-weight: 700;


 .ssi-title-lead,
 .ssi-title-product {
   display: block;
   font-size: inherit;
   line-height: inherit;
   font-weight: inherit;
 }


 .ssi-title-product {
   //  margin-top: 4px;
 }
`;


export const WeliregColumnTitle = styled(ColumnTitle)`
 color: ${colors.weliregBlue};
`;


export const KeytrudaColumnTitle = styled(ColumnTitle)`
 color: #008542;


 .purple-highlight {
   color: #8c2d8c;
 }
`;


export const ColumnWarningTitle = styled(Text.H6)`
 font-size: 16px;
 line-height: 20px;
 letter-spacing: -0.16px;
 color: #000;
 margin: 0;
 font-weight: 700;
 text-transform: uppercase;
`;


export const WarningBlock = styled.div`
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 align-self: stretch;
 gap: 8px;
 padding: 0;
 margin: 0;
 width: 100%;
 box-sizing: border-box;
`;


export const Section = styled.section`
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 align-self: stretch;
 gap: ${({ $titleGap }) => $titleGap || '16px'};
 padding-bottom: ${({ $paddingBottom }) => $paddingBottom || 0};
 margin: 0;
 width: 100%;


 ${BulletList},
 ${KeytrudaBulletList} {
   ${({ $listGap }) => ($listGap ? `gap: ${$listGap};` : '')}
   ${({ $listPaddingBottom }) =>
     $listPaddingBottom ? `padding-bottom: ${$listPaddingBottom};` : ''}
 }


 ${BulletList} li,
 ${KeytrudaBulletList} li {
   ${({ $itemPaddingBottom }) =>
     $itemPaddingBottom
       ? `
     padding-bottom: ${$itemPaddingBottom};


     &:last-child {
       padding-bottom: 0;
     }
   `
       : ''}
 }
`;


export const SectionTitle = styled(Text.H6)`
 font-size: 16px;
 line-height: 20px;
 letter-spacing: -0.16px;
 margin: 0;
 margin-bottom: 0;
 color: ${colors.black};
 font-weight: 700;


 .mt-minus {
   margin-top: -4px;
   display: block;
 }
`;


export const WeliregSectionTitle = styled(SectionTitle)`
 color: ${colors.weliregBlue};
`;


export const SubSection = styled.div`
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 align-self: stretch;
 gap: ${({ $gap }) => $gap || '4px'};
 padding-bottom: ${({ $paddingBottom }) => $paddingBottom || 0};
 margin: 0;
 width: 100%;


 & + & {
   margin-top: ${({ $siblingGap, $major }) =>
     $siblingGap !== undefined ? $siblingGap : $major ? '12px' : '4px'};
 }
`;


export const SubSectionTitle = styled(Text.H6)`
 font-size: ${safetyFontSize};
 margin: 0;
 color: #000;
 font-weight: 400;
 text-decoration: underline;
 text-underline-offset: 2px;
`;


// WELIREG Adverse Reactions study titles
export const WeliregSubSectionTitle = styled(SubSectionTitle)`
 font-weight: 400;
`;


export const SubSectionItalicTitle = styled(Text.H6)`
 font-size: ${safetyFontSize};
 margin: 0;
 color: #000000;
 font-style: italic;
 font-weight: 400;
`;


export const SafetyJobCode = styled(Text.Footnote)`
 display: block;
 margin-top: 18px;
 color: ${colors.black};
 text-align: right;
`;


export const FinalWarningWrapper = styled(Text.Body)`
 margin: 0;
 font-size: ${safetyFontSize};
 line-height: ${safetyLineHeight};
 font-weight: 700;
 color: ${colors.black};


 /* Beat Text.Body getLinkStyles (ruby hover) regardless of SC injection order */
 && a {
   color: ${colors.weliregBlue};
   text-decoration: underline;


   &:hover,
   &:active,
   &:focus,
   &:visited {
     color: ${colors.weliregBlue};
   }


   &:hover {
     text-decoration: none;
   }


   &.green-highlight,
   &.green-highlight:hover,
   &.green-highlight:active,
   &.green-highlight:focus,
   &.green-highlight:visited {
     color: ${colors.closeGreen};
   }


   &.purple-highlight,
   &.purple-highlight:hover,
   &.purple-highlight:active,
   &.purple-highlight:focus,
   &.purple-highlight:visited {
     color: #7C02BB;
   }
 }
`;


// Desktop: WELIREG PI block stays in left column
export const WeliregFinalWarningDesktop = styled(FinalWarningWrapper)`
 display: none;


 ${respondTo.md`
   display: block;
 `}
`;


// Mobile: WELIREG PI before KEYTRUDA PI (KeytrudaColumnFooter gap 10px)
export const WeliregFinalWarningMobile = styled(FinalWarningWrapper)`
 display: block;


 ${respondTo.md`
   display: none;
 `}
`;


export const IndicationsFullWidth = styled.div`
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 align-self: stretch;
 gap: 40px;
 width: 100%;
 max-width: 984px;
 padding: 0;
 box-sizing: border-box;
 background: ${colors.white};
`;


export const IndicationsDividerLeft = styled(Text.Body)`
 margin-top: -4px;
 display: none;
 font-size: ${safetyFontSize};
 line-height: ${safetyLineHeight};
 font-weight: 700;
 color: ${colors.black};
 text-align: left;
 margin-bottom: 0px;


 ${respondTo.md`
   display: block;
 `}
`;


// WELIREG PI + "Scroll below for Indications" — desktop only (same pattern as IndicationsDividerLeft)
export const WeliregColumnFooter = styled.div`
 display: none;
 flex-direction: column;
 align-items: flex-start;
 gap: 12px;
 flex: 1 0 0;
 align-self: stretch;
 width: 100%;


 > * {
   margin-top: 0;
   margin-bottom: 0;
 }


 && .spacing-expand {
   margin-top: 0;
 }


 ${respondTo.md`
   display: flex;
 `}
`;


// Mobile: gap 10px between PI texts; margin-top keeps Lactation → PI at 10px (column gap is 16px)
// Desktop: gap 12px to "Scroll below for Indications"
export const KeytrudaColumnFooter = styled(WeliregColumnFooter)`
 display: flex;
 gap: 10px;
 margin-top: -6px;


 ${respondTo.md`
   margin-top: 0;
   gap: 12px;
 `}
`;


export const IndicationsDividerRight = styled(IndicationsDividerLeft)`
 margin-top: 0;
`;


export const IndicationSection = styled.section`
 display: flex;
 flex-direction: column;
 align-items: flex-start;
 align-self: stretch;
 margin: 0;
 width: 100%;
`;


// "Indication for WELIREG in combination with KEYTRUDA or KEYTRUDA QLEX"
export const IndicationHeading = styled(Text.H6)`
 color: #414042;
 font-size: 24px;
 font-style: normal;
 font-weight: 700;
 line-height: 24px;
 letter-spacing: -0.24px;
 margin-bottom: 10px;


 .blue-highlight {
   color: #2E3790;
   font-size: 24px;
   font-style: normal;
   font-weight: 700;
   line-height: 24px;
   letter-spacing: -0.24px;
 }


 .green-highlight {
   color: #2C6D3C;
   font-size: 24px;
   font-style: normal;
   font-weight: 700;
   line-height: 24px;
   letter-spacing: -0.24px;
 }


 .purple-highlight {
   color: #7C02BB;
   font-size: 24px;
   font-style: normal;
   font-weight: 700;
   line-height: 24px;
   letter-spacing: -0.24px;
 }
`;


export const WeliregIndicationsHeading = styled(Text.H6)`
 color: ${colors.weliregBlue};
 font-size: 24px;
 margin-bottom: 10px;
 font-weight: 700;
 line-height: 24px;
 letter-spacing: -0.24px;
`;


export const IndicationBody = styled(Text.Body)`
 font-size: ${isiBodyFontSize};
 line-height: calc(1em + 6px);
 color: ${colors.black};
 margin-bottom: 0;


 p {
   margin-bottom: 20px;
   font-size: inherit;
   line-height: inherit;
 }


 p:last-child {
   margin-bottom: 0;
 }
`;


export const CenterColumn = styled.div`
 margin-bottom: 0;
 width: 100%;


 .top-title {
   color: ${colors.brandBlueDark};
   text-align: center;
   font-size: 16px;
   font-weight: 700;
   line-height: 20px;
 }
`;


// column separator — centered in the 40px gap (desktop only)
export const ColumnDivider = styled.div`
 display: none;


 ${respondTo.md`
   display: block;
   position: absolute;
   left: 50%;
   top: 0;
   bottom: 0;
   width: 1px;
   transform: translateX(-50%);
   background: #E1E1E1;
   pointer-events: none;
 `}
`;
